import { useEffect, useMemo, useRef, useState } from 'react';
import { initTextToSpeech, speak, stopSpeaking } from '../../../lib/textToSpeech';
import { requestWakeLock, releaseWakeLock, setupWakeLockReacquisition } from '../../../lib/wakeLock';
import { generateWorkoutPlaylist, getCardSpeechText, type WorkoutCard } from './workoutCards';
import type { SetLogRecord, WorkoutSession } from '../../../types';

interface UseWorkoutPlaybackArgs {
  session: WorkoutSession | null;
  logHistory: Record<string, SetLogRecord[]>;
}

export function useWorkoutPlayback({ session, logHistory }: UseWorkoutPlaybackArgs) {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    try {
      return localStorage.getItem('gf_tts_enabled') === 'true';
    } catch {
      return false;
    }
  });
  const [restTimerStates, setRestTimerStates] = useState<Record<string, number>>({});
  const speechRequestIdRef = useRef(0);
  const isPlayingRef = useRef(isPlaying);
  const cardsLengthRef = useRef(0);

  // Generate workout playlist
  const cards: WorkoutCard[] = useMemo(() => {
    if (!session) return [];
    return generateWorkoutPlaylist(session, logHistory);
  }, [session, logHistory]);

  const currentCard = cards[currentCardIndex];
  const shouldAdvanceOnSpeechEnd = currentCard?.type === 'exercise_info' || currentCard?.type === 'cues';
  const isCompletionScreen = currentCardIndex >= cards.length;

  // Sync refs
  useEffect(() => {
    isPlayingRef.current = isPlaying;
    cardsLengthRef.current = cards.length;
  }, [isPlaying, cards.length]);

  // Initialize TTS and wake lock
  useEffect(() => {
    const available = initTextToSpeech();
    if (!available) {
      console.warn('Text-to-speech not available');
    }

    requestWakeLock();
    setupWakeLockReacquisition();

    return () => {
      releaseWakeLock();
    };
  }, []);

  // Auto-speak card content when changing cards
  useEffect(() => {
    if (!currentCard || !ttsEnabled || !isPlaying) return;

    const text = getCardSpeechText(currentCard);
    if (!text) return;

    const requestId = ++speechRequestIdRef.current;
    const shouldAdvanceThisCard = shouldAdvanceOnSpeechEnd;

    speak(text, {
      onEnd: () => {
        if (speechRequestIdRef.current !== requestId || !isPlayingRef.current || !shouldAdvanceThisCard) {
          return;
        }

        setCurrentCardIndex((prev) => {
          if (prev < cardsLengthRef.current - 1) {
            return prev + 1;
          }

          setIsPlaying(false);
          return prev;
        });
      },
    });

    return () => {
      speechRequestIdRef.current += 1;
      stopSpeaking();
    };
  }, [currentCard, ttsEnabled, isPlaying, shouldAdvanceOnSpeechEnd]);

  // Auto-advance mode
  useEffect(() => {
    if (!isPlaying || !currentCard?.autoDuration) return;

    const canAutoAdvance = currentCard.type !== 'set';
    const waitForSpeechToAdvance = ttsEnabled && shouldAdvanceOnSpeechEnd;

    if (!canAutoAdvance || waitForSpeechToAdvance) return;

    const timer = setTimeout(() => {
      if (currentCardIndex < cards.length - 1) {
        setCurrentCardIndex((prev) => prev + 1);
      } else {
        setIsPlaying(false);
      }
    }, currentCard.autoDuration * 1000);

    return () => clearTimeout(timer);
  }, [isPlaying, currentCard, currentCardIndex, cards.length, ttsEnabled, shouldAdvanceOnSpeechEnd]);

  const handleTogglePlay = () => {
    setIsPlaying((prev) => !prev);
    if (isPlaying) {
      stopSpeaking();
    }
  };

  const handleToggleTts = () => {
    setTtsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('gf_tts_enabled', String(next));
      } catch { /* ignore */ }
      if (!next) stopSpeaking();
      return next;
    });
  };

  return {
    cards,
    currentCard,
    currentCardIndex,
    setCurrentCardIndex,
    isPlaying,
    isCompletionScreen,
    playlistOpen,
    setPlaylistOpen,
    ttsEnabled,
    restTimerStates,
    setRestTimerStates,
    handleTogglePlay,
    handleToggleTts,
  };
}
